import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { Chip } from "../../../../components/common/chip";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { GatedPrice } from "../../../../components/common/gated-price";
import { Button } from "../../../../components/common/button";
import { Image } from "../../../../components/common/image";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Heading, Text } from "../../../../components/common/typography";
import { useBestDeals } from "../../hooks/useBestDeals";
import { useFeaturedDeals } from "../../../featured-deals/hooks/useFeaturedDeals";
import { FeaturedDealsRow } from "../../../featured-deals/presentation/components/featured-deals-row";

export function BasketBestDeals() {
  const addToCart = useCartStore((s) => s.addToCart);
  const items = useCartStore((s) => s.items);
  const excludeIds = items.map((item) => item.product.id);

  // Prefer admin-curated featured deals (Deal of the Week). Falls back to the
  // stock+price heuristic feed when nothing has been curated yet.
  const { data: featured, isLoading: featuredLoading } = useFeaturedDeals();
  const hasCurated = (featured ?? []).some((d) => d.product != null);

  const { data, isLoading, error } = useBestDeals({
    limit: 3,
    excludeIds: excludeIds.length > 0 ? excludeIds.join(",") : undefined,
  });

  if (featuredLoading) return null;
  if (hasCurated) {
    return <FeaturedDealsRow className="mt-4" limit={3} />;
  }

  const deals = data?.items ?? [];

  if (!isLoading && (error || deals.length === 0)) return null;

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-col gap-1.5 text-center">
        <Heading variant="h3" tone="dark">Our Best Deals</Heading>
        <Text variant="bodySm" tone="dark">
          Handpicked offers from verified suppliers - updated daily
        </Text>
        {data?.updatedAt && (
          <Text variant="bodyXs" tone="muted">
            Refreshed {new Date(data.updatedAt).toLocaleDateString()}
          </Text>
        )}
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading best deals…" lines={3} className="rounded-2xl border border-borderDefault bg-bgWhite p-4" />
      ) : (
        <div className="flex flex-col gap-2">
          {deals.map(({ product, warrantyLabel, supplierName, supplierVerified, discountPercent }) => {
            const discount =
              discountPercent ??
              (product.standardPrice && product.listPrice
                ? Math.round((1 - product.listPrice / product.standardPrice) * 100)
                : null);

            return (
              <div
                key={product.id}
                className="flex items-center gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-4"
              >
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-bgLight">
                  <Image
                    image={product.image}
                    alt={product.displayName ?? product.name}
                    size="w-full h-full"
                    showPlaceholder
                  />
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <Text variant="bodyBold" tone="dark">
                    {product.displayName ?? product.name}
                  </Text>
                  {product.description && (
                    <Text variant="bodyXs" tone="dark">
                      {product.description}
                    </Text>
                  )}
                  <Text variant="bodyXs" tone="muted">
                    {supplierName}
                    {supplierVerified ? " · verified supplier" : ""}
                  </Text>
                  <Text variant="bodyXs" tone="muted">{warrantyLabel}</Text>
                  <div className="mt-0.5 flex items-center gap-2">
                    <CurrencyAmount
                      amount={product.listPrice ?? 0}
                      currency="QAR"
                      variant="bodySmBold"
                      tone="dark"
                    />
                    {product.standardPrice != null && product.standardPrice > 0 && (
                      <Text variant="bodyXs" tone="dark" className="line-through">
                        <GatedPrice>
                          QAR {product.standardPrice.toLocaleString()}
                        </GatedPrice>
                      </Text>
                    )}
                    <Text variant="bodyXs" tone="muted">Pre-Order</Text>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {discount !== null && (
                    <Chip variant="secondary">{discount}% off</Chip>
                  )}
                  <Button variant="primary" size="sm" onClick={() => addToCart(product, 1)}>
                    Add to cart
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
